import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { hrsService } from "../../services/hrs.services";
import { IBuildingWithSalesUnits } from "../../domain/IBuildingWithSalesUnits";
import { IBuildingUpdate } from "../../domain/IBuildingUpdate";
import { ISalesUnitUpdate } from "../../domain/ISalesUnitUpdate";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { getIndexById, getNextSortOrder, ISortParams, sort } from "utils/sortUtils";
import { IGetBuildingsListRequestParams } from "../../domain/IGetBuildingsListRequestParams";
import { IStatus } from "Dashboard/domain/IStatus";

export const getBuildingsList = createAsyncThunk(
  "Buildings/getBuildingsList",
  async (data: IGetBuildingsListRequestParams) => {
    try {
      return await hrsService.getAllBuildings(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteBuilding = createAsyncThunk(
  "Buildings/deleteBuilding",
  async (buildingId: number, { rejectWithValue }) => {
    try {
      return {
        response: await hrsService.deleteBuilding(buildingId),
        deletedBuildingId: buildingId
      };
    } catch (err: any) {
      return rejectWithValue(err.status);
    }
  }
);

export const deleteSalesUnit = createAsyncThunk(
  "SalesUnits/deleteSalesUnit",
  async (data: any, { rejectWithValue }) => {
    try {
      return await hrsService.deleteSalesUnit(data.buildingId, data.salesUnitId);
    } catch (err: any) {
      return rejectWithValue(err.status);
    }
  }
);

export const getSalesUnitStatuses = createAsyncThunk(
  "SalesUnits/getSalesUnitStatuses",
  async (_, { rejectWithValue }) => {
    try {
      return await hrsService.getSalesUnitStatus();
    } catch (err: any) {
      return rejectWithValue(err.status);
    }
  }
);

interface mainState {
  loading: boolean;
  buildings: IBuildingWithSalesUnits[];
  sort: ISortParams | undefined;
  selectedBuilding: number;
  expandedBuilding: number | undefined;
  selectedSalesUnit: number;
  buildingForDeletion: number;
  salesUnitForDeletion: number;
  newBuildingPopover: IPopoverState;
  newSalesUnitPopover: IPopoverState;
  searchPopover: IPopoverState;
  deleteItemModalOpen: boolean;
  error: any;
  statuses: IStatus[];
}

const initialState: mainState = {
  loading: false,
  buildings: [],
  sort: undefined,
  selectedBuilding: 0,
  expandedBuilding: 0,
  selectedSalesUnit: 0,
  buildingForDeletion: -1,
  salesUnitForDeletion: -1,
  newBuildingPopover: { isOpen: false },
  newSalesUnitPopover: {
    isOpen: false,
    top: 0,
    left: 0
  },
  searchPopover: { isOpen: false },
  deleteItemModalOpen: false,
  error: null,
  statuses: [],
};

export const mainSlice = createSlice({
  name: "buildings-main",
  initialState: initialState,
  reducers: {
    setSort: (state, action: PayloadAction<ISortParams>) => {
      const newSortOrder = getNextSortOrder(state.sort, action.payload.key);
      const newSortParams = {
        key: action.payload.key,
        typeOfValue: action.payload.typeOfValue,
        order: newSortOrder
      };
      let selectedProjectId = undefined;
      if (state.buildings.length > 0) {
        selectedProjectId = state.buildings[state.selectedBuilding].building.buildingId;
      }
      state.sort = newSortParams;
      state.buildings = sort(state.buildings, newSortParams);
      state.selectedBuilding = getIndexById(
        state.buildings,
        selectedProjectId,
        (project: IBuildingWithSalesUnits) => project.building.buildingId);
    },
    setSelectedBuilding: (state, action: PayloadAction<number>) => {
      state.selectedBuilding = action.payload;
      state.selectedSalesUnit = -1;
    },
    setSelectedSalesUnit: (state, action: PayloadAction<number>) => {
      state.selectedSalesUnit = action.payload;
    },
    setExpandingBuilding: (state, action: PayloadAction<number>) => {
      state.expandedBuilding = action.payload;
    },
    setNewBuildingPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newBuildingPopover = action.payload;
    },
    setNewSalesUnitPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newSalesUnitPopover = action.payload;
    },
    setSearchHRSPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchPopover = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setDeleteBuildingModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteItemModalOpen = action.payload.visible;
      state.buildingForDeletion = action.payload.index;
    },
    setDeleteSalesUnitModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteItemModalOpen = action.payload.visible;
      state.salesUnitForDeletion = action.payload.index;
    },
    setBuilding: (state, action: PayloadAction<IBuildingUpdate>) => {
      if (action.payload) {
        state.buildings = state.buildings.map((building) =>
          building.building.buildingId === action.payload.buildingId
            ? {
              ...building,
              building: { ...building.building, ...action.payload },
            }
            : building
        );
      }
    },
    setBuildings: (state, action: PayloadAction<IBuildingWithSalesUnits[]>) => {
      state.buildings = action.payload;
    },
    updateBuildingWithSalesUnits: (state, action: PayloadAction<IBuildingWithSalesUnits>) => {
      let index = state.buildings.findIndex(building =>
        building.building.buildingId === action.payload.building.buildingId);

      state.buildings[index] = action.payload;
    },
    setSalesUnit: (state, action: PayloadAction<ISalesUnitUpdate>) => {
      if (action.payload) {
        for (let i = 0; i < state.buildings.length; i++) {
          if (state.buildings[i].building.buildingId === action.payload.buildingId) {
            state.buildings[i] = {
              ...state.buildings[i],
              salesUnits: state.buildings[i].salesUnits.map((salesUnit) => {
                if (salesUnit.salesUnitId !== action.payload.salesUnitId)
                  return salesUnit;

                let { salesUnitTypeId, buyerPartnerId, ...otherprops } = action.payload;

                if (typeof salesUnitTypeId === "object") {
                  salesUnit.salesUnitType = salesUnitTypeId;
                }
                return { ...salesUnit, ...otherprops };
              }),
            }
          }
        }
      }
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    updateStatuses: (state, { payload }: PayloadAction<IStatus>) => {
      state.statuses.push(payload);
    },

  },
  extraReducers: (builder) => {
    builder.addCase(getBuildingsList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBuildingsList.fulfilled, (state, action) => {
      state.loading = false;
      state.buildings = action.payload?.buildings
        ? action.payload.buildings
        : [];
      state.selectedBuilding = 0;
      if (state.buildings.length > 0) {
        state.expandedBuilding =
          action.payload?.buildings[0].building.buildingId;
      }
      state.selectedSalesUnit = -1;
    });
    builder.addCase(getBuildingsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(deleteBuilding.fulfilled, (state, action) => {
      if (action.payload) {
        state.buildings = state.buildings.filter(building =>
          building.building.buildingId !== action.payload.deletedBuildingId
        );
      }
    });
    builder.addCase(deleteSalesUnit.fulfilled, (state, action) => {
      if (action.payload) {
        let index = state.buildings.findIndex(building =>
          building.building.buildingId === action.payload.building.buildingId);

        state.buildings[index] = action.payload;
      }
    });

    builder.addCase(getSalesUnitStatuses.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getSalesUnitStatuses.fulfilled, (state, action) => {
      state.loading = false;
      state.statuses = action.payload || [];
    });

    builder.addCase(getSalesUnitStatuses.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
  },
});

export const selectState = (state: RootState) => state.hrs.main;
export const selectBuilding = (state: RootState) =>
  state.hrs.main.buildings[state.hrs.main.selectedBuilding];
export const selectSalesUnit = (state: RootState) =>
  state.hrs.main.buildings[state.hrs.main.selectedBuilding]?.salesUnits[
  state.hrs.main?.selectedSalesUnit
  ];
export const selectBuildingForDeletion = (state: RootState) =>
  state.hrs.main.buildings[state.hrs.main.buildingForDeletion];
export const selectSalesUnitForDeletion = (state: RootState) =>
  state.hrs.main.buildings[state.hrs.main.selectedBuilding]?.salesUnits[
  state.hrs.main.salesUnitForDeletion
  ];

export const {
  setLoading,
  setError,
  setSort,
  setBuilding,
  setSalesUnit,
  updateBuildingWithSalesUnits,
  setSelectedBuilding,
  setExpandingBuilding,
  setSelectedSalesUnit,
  setDeleteBuildingModalOpen,
  setDeleteSalesUnitModalOpen,
  setNewBuildingPopover,
  setNewSalesUnitPopover,
  setSearchHRSPopover,
  updateStatuses
} = mainSlice.actions;

export default mainSlice.reducer;
